import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Music, Wand2, Film, Sparkles, UploadCloud, Play, Loader2, Trash2, ImagePlus, X, Download, Music2, UserRound, CheckCircle2, Lock, Crown } from "lucide-react";
import Nav from "@/components/Nav";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const AR_OPTIONS = [
  { label: "16:9 HD", value: "1280x720" },
  { label: "9:16 Vertical", value: "1024x1792" },
  { label: "1:1 Square", value: "1024x1024" },
  { label: "Widescreen", value: "1792x1024" },
];

const GENRES = ["pop", "hip-hop", "rock", "electronic", "r&b", "indie", "metal", "country"];
const MOODS = ["energetic", "dreamy", "dark", "romantic", "cinematic", "playful"];

export default function Studio() {
  const { user, refresh } = useAuth();
  const isPro = !!user?.is_pro;
  const freeRendersLeft = user?.free_renders_left ?? 1;
  const songMax = isPro ? (user?.limits?.pro_song_max_seconds ?? 240) : (user?.limits?.free_song_max_seconds ?? 30);
  const [prompt, setPrompt] = useState("");
  const [genre, setGenre] = useState("pop");
  const [mood, setMood] = useState("energetic");
  const [duration, setDuration] = useState(12);
  const [aspect, setAspect] = useState("1280x720");
  const [title, setTitle] = useState("");
  const [scenes, setScenes] = useState([]);
  const [lyrics, setLyrics] = useState("");
  const [audio, setAudio] = useState(null); // { id, name }
  const [songLength, setSongLength] = useState(30); // seconds — free 30s preview, PRO up to 240 (4 min)
  const [images, setImages] = useState([]); // [{id, name}]
  const [faceRef, setFaceRef] = useState(null); // { imageId, description, loading }
  const [busy, setBusy] = useState({ storyboard: false, lyrics: false, upload: false, render: false, music: false });
  const [job, setJob] = useState(null); // { job_id, status, progress, clip_file_ids, ... }
  const [musicJob, setMusicJob] = useState(null); // { job_id, status, progress, audio_file_id }
  const pollRef = useRef(null);
  const musicPollRef = useRef(null);
  const audioInputRef = useRef(null);
  const imageInputRef = useRef(null);

  const canGenerateStoryboard = prompt.trim().length > 6;
  const canRender = scenes.length > 0 && title.trim().length > 0;
  const renderLocked = !isPro && freeRendersLeft <= 0;

  useEffect(() => {
    if (songLength > songMax) setSongLength(songMax);
  }, [songMax, songLength]);

  const proUpsell = (msg) =>
    toast.error(msg || "PRO subscription required", {
      action: { label: "Go Pro · $15.99", onClick: () => (window.location.href = "/pricing") },
    });

  const isProError = (e) => e?.response?.status === 402;

  const generateStoryboard = async () => {
    if (!canGenerateStoryboard) return toast.error("Describe your song idea first.");
    setBusy((b) => ({ ...b, storyboard: true }));
    try {
      const body = { prompt, genre, mood, duration, aspect_ratio: aspect };
      if (faceRef?.description) body.character_description = faceRef.description;
      const { data } = await api.post("/storyboard", body);
      setScenes(data.scenes);
      setTitle(data.title || "Untitled");
      toast.success(`Storyboard: ${data.scenes.length} scenes${faceRef ? " (with face reference)" : ""}`);
    } catch (e) {
      toast.error("Storyboard failed. Try again.");
    } finally {
      setBusy((b) => ({ ...b, storyboard: false }));
    }
  };

  const setAsFaceReference = async (imageId) => {
    if (faceRef?.imageId === imageId) {
      setFaceRef(null);
      toast.info("Face reference removed");
      return;
    }
    setFaceRef({ imageId, description: null, loading: true });
    try {
      const { data } = await api.post("/character/describe", { image_file_id: imageId });
      setFaceRef({ imageId, description: data.description, loading: false });
      toast.success("Face reference set — will appear in every scene");
    } catch (e) {
      setFaceRef(null);
      toast.error("Could not describe face");
    }
  };

  const generateLyrics = async () => {
    if (!canGenerateStoryboard) return toast.error("Give a theme in the prompt first.");
    setBusy((b) => ({ ...b, lyrics: true }));
    try {
      const { data } = await api.post("/lyrics", { theme: prompt, genre, mood });
      setLyrics(data.lyrics);
      toast.success("Lyrics generated");
    } catch (e) {
      toast.error("Lyrics failed.");
    } finally {
      setBusy((b) => ({ ...b, lyrics: false }));
    }
  };

  const onUpload = async (file, kind) => {
    setBusy((b) => ({ ...b, upload: true }));
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post(`/upload/${kind}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (kind === "audio") {
        setAudio({ id: data.id, name: file.name });
        toast.success("Audio uploaded");
      } else {
        setImages((imgs) => [...imgs, { id: data.id, name: file.name }]);
        toast.success("Image uploaded");
      }
    } catch (e) {
      toast.error(`Upload failed: ${file.name}`);
    } finally {
      setBusy((b) => ({ ...b, upload: false }));
    }
  };

  const updateScene = (idx, key, val) => {
    setScenes((s) => s.map((x, i) => (i === idx ? { ...x, [key]: val } : x)));
  };

  const removeScene = (idx) => setScenes((s) => s.filter((_, i) => i !== idx));

  const renderVideo = async () => {
    if (!canRender) return toast.error("Add at least one scene.");
    setBusy((b) => ({ ...b, render: true }));
    try {
      const { data } = await api.post("/videos/generate", {
        title,
        scenes: scenes.map((s, i) => ({ order: i + 1, prompt: s.prompt, duration: s.duration })),
        aspect_ratio: aspect,
        model: "sora-2",
        audio_file_id: audio?.id || null,
        image_file_ids: images.map((i) => i.id),
        lyrics: lyrics || null,
        character_description: faceRef?.description || null,
      });
      setJob({ job_id: data.job_id, status: "queued", progress: 0, clip_file_ids: [] });
      toast.success("Rendering started (this may take several minutes)");
      refresh();
    } catch (e) {
      if (isProError(e)) proUpsell(e.response?.data?.detail?.message);
      else toast.error("Failed to start render");
    } finally {
      setBusy((b) => ({ ...b, render: false }));
    }
  };

  // Poll job status
  useEffect(() => {
    if (!job?.job_id || job.status === "ready" || job.status === "failed") {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/videos/${job.job_id}`);
        setJob(data);
        if (data.status === "ready") toast.success("Video is ready");
        if (data.status === "failed") toast.error("Render failed");
      } catch (e) {
        // ignore transient
      }
    }, 5000);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [job?.job_id, job?.status]);

  // Music generation
  const generateMusic = async () => {
    if (!canGenerateStoryboard) return toast.error("Add a song idea in the prompt first.");
    setBusy((b) => ({ ...b, music: true }));
    try {
      const { data } = await api.post("/music/generate", {
        prompt, genre, mood, seconds_total: songLength,
      });
      setMusicJob({ job_id: data.job_id, status: "queued", progress: 0 });
      toast.success(`AI music generation started (${songLength}s track)`);
    } catch (e) {
      if (isProError(e)) proUpsell(e.response?.data?.detail?.message);
      else toast.error("Could not start music generation");
    } finally {
      setBusy((b) => ({ ...b, music: false }));
    }
  };

  // Poll music job
  useEffect(() => {
    if (!musicJob?.job_id || musicJob.status === "ready" || musicJob.status === "failed") {
      if (musicPollRef.current) clearInterval(musicPollRef.current);
      return;
    }
    musicPollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/music/${musicJob.job_id}`);
        setMusicJob(data);
        if (data.status === "ready" && data.audio_file_id) {
          setAudio({ id: data.audio_file_id, name: `ai-track.wav` });
          toast.success("AI track ready — attached to your video");
        }
        if (data.status === "failed") toast.error(data.error?.slice(0, 100) || "Music generation failed");
      } catch (e) {
        // ignore
      }
    }, 4000);
    return () => musicPollRef.current && clearInterval(musicPollRef.current);
  }, [musicJob?.job_id, musicJob?.status]);

  const totalDuration = useMemo(
    () => scenes.reduce((s, sc) => s + (sc.duration || 0), 0),
    [scenes]
  );

  return (
    <div>
      <Nav />
      <div className="mx-auto max-w-[1500px] px-4 py-6 pb-28 md:px-6 md:py-8 md:pb-8">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4 md:mb-8">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FFE800]">// The Studio</div>
            <h1 className="font-display text-5xl leading-none tracking-tight sm:text-6xl md:text-7xl">Build a Music Video</h1>
          </div>
          <div className="hidden font-mono text-xs uppercase tracking-widest text-[#808080] sm:block">
            Model: sora-2 // Storyboard: claude-sonnet-5
          </div>
        </div>

        <div className="grid gap-0 md:grid-cols-12">
          {/* LEFT: inputs */}
          <div className="frame md:col-span-5">
            <div className="border-b border-[#222] p-5">
              <div className="font-mono text-xs uppercase tracking-widest text-[#808080]">01 / Concept</div>
              <label className="mt-3 block font-display text-3xl">Song Idea</label>
              <textarea
                data-testid="studio-prompt-input"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={4}
                placeholder="e.g. A late-night drive through Tokyo neon rain, chasing memories of a lost love."
                className="mt-3 w-full border border-[#222] bg-[#050505] p-4 font-mono text-sm text-white outline-none focus:border-[#FF003C]"
              />
              <div className="mt-4 grid grid-cols-2 gap-3">
                <Selector label="Genre" value={genre} onChange={setGenre} options={GENRES} testid="studio-genre" />
                <Selector label="Mood" value={mood} onChange={setMood} options={MOODS} testid="studio-mood" />
                <Selector
                  label="Aspect"
                  value={aspect}
                  onChange={setAspect}
                  options={AR_OPTIONS.map((o) => o.value)}
                  render={(v) => AR_OPTIONS.find((o) => o.value === v)?.label || v}
                  testid="studio-aspect"
                />
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#808080]">Duration (sec)</div>
                  <input
                    data-testid="studio-duration"
                    type="number"
                    min={4}
                    max={60}
                    step={4}
                    value={duration}
                    onChange={(e) => setDuration(Number(e.target.value))}
                    className="mt-1 w-full border border-[#222] bg-[#050505] p-2 font-mono text-sm text-white focus:border-[#FF003C] outline-none"
                  />
                </div>
              </div>
              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  data-testid="studio-generate-storyboard-btn"
                  onClick={generateStoryboard}
                  disabled={busy.storyboard}
                  className="btn-brutal"
                >
                  {busy.storyboard ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />}
                  Generate Storyboard
                </button>
                <button
                  data-testid="studio-generate-lyrics-btn"
                  onClick={generateLyrics}
                  disabled={busy.lyrics}
                  className="btn-brutal secondary"
                >
                  {busy.lyrics ? <Loader2 className="animate-spin" size={16} /> : <Music size={16} />}
                  Generate Lyrics
                </button>
              </div>
            </div>

            <div className="border-b border-[#222] p-5">
              <div className="font-mono text-xs uppercase tracking-widest text-[#808080]">02 / Audio</div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                <div className="font-display text-2xl">Your Track</div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    data-testid="studio-generate-music-btn"
                    onClick={generateMusic}
                    className="btn-ghost"
                    disabled={busy.music || (musicJob && musicJob.status !== "ready" && musicJob.status !== "failed")}
                  >
                    {busy.music || (musicJob && ["queued","generating"].includes(musicJob.status))
                      ? <Loader2 className="animate-spin" size={14} />
                      : <Music2 size={14} />}
                    AI Generate
                  </button>
                  <button
                    data-testid="studio-upload-audio-btn"
                    onClick={() => audioInputRef.current?.click()}
                    className="btn-ghost"
                    disabled={busy.upload}
                  >
                    <UploadCloud size={14} /> Upload
                  </button>
                </div>
                <input
                  ref={audioInputRef}
                  type="file"
                  accept="audio/*"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0], "audio")}
                />
              </div>

              {/* Song length slider — only relevant to AI generate */}
              <div className="mt-4 border border-[#222] p-3" data-testid="studio-song-length-wrap">
                <div className="flex items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-widest text-[#808080]">
                  <span>Song Length {!isPro && <span className="text-[#FFE800]">· Free 30s</span>}</span>
                  <span data-testid="studio-song-length-value" className="shrink-0 text-[#00F0FF]">
                    {Math.floor(songLength / 60)}:{String(songLength % 60).padStart(2, "0")} ({songLength}s)
                  </span>
                </div>
                <input
                  type="range"
                  min={isPro ? 30 : 10}
                  max={songMax}
                  step={isPro ? 15 : 10}
                  value={songLength}
                  onChange={(e) => setSongLength(Number(e.target.value))}
                  data-testid="studio-song-length-slider"
                  className="mt-2 w-full accent-[#FF003C]"
                />
                <div className="mt-1 flex justify-between font-mono text-[9px] text-[#808080]">
                  <span>{isPro ? "0:30" : "0:10"}</span>
                  <span>{isPro ? "2:00" : "0:20"}</span>
                  <span>{isPro ? "4:00" : "0:30"}</span>
                </div>
                {!isPro && (
                  <Link
                    to="/pricing"
                    data-testid="studio-song-length-upsell"
                    className="mt-3 flex items-center justify-between gap-3 border border-[#FFE800] bg-[#FFE800]/10 px-3 py-2.5 font-mono text-[10px] uppercase tracking-widest text-[#FFE800] transition-colors hover:bg-[#FFE800] hover:text-black"
                  >
                    <span className="flex items-center gap-2"><Lock size={12} className="shrink-0" /> 4-min songs</span>
                    <span className="flex shrink-0 items-center gap-1"><Crown size={12} /> Go Pro</span>
                  </Link>
                )}
              </div>
              {musicJob && musicJob.status !== "ready" && (
                <div className="mt-3 border border-[#222] p-2">
                  <div className="flex justify-between font-mono text-[10px] uppercase tracking-widest text-[#808080]">
                    <span>AI Music: {musicJob.status}</span>
                    <span>{musicJob.progress || 0}%</span>
                  </div>
                  <div className="mt-1 h-1 w-full bg-[#222]">
                    <div className="h-1 bg-[#00F0FF] transition-all duration-500" style={{ width: `${musicJob.progress || 0}%` }} />
                  </div>
                  {musicJob.error && (
                    <div className="mt-2 font-mono text-[10px] text-[#FF003C]">{musicJob.error}</div>
                  )}
                </div>
              )}
              {audio && (
                <div className="mt-3 flex items-center justify-between border border-[#222] p-2">
                  <div className="truncate font-mono text-xs text-white">{audio.name}</div>
                  <div className="flex items-center gap-2">
                    <a
                      href={`${API}/files/${audio.id}/download`}
                      className="text-[#808080] hover:text-[#00F0FF]"
                      title="Download"
                      data-testid="studio-download-audio-btn"
                    >
                      <Download size={14} />
                    </a>
                    <button onClick={() => setAudio(null)} className="text-[#808080] hover:text-[#FF003C]">
                      <X size={14} />
                    </button>
                  </div>
                </div>
              )}
              {lyrics && (
                <div className="mt-4">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#808080]">Lyrics</div>
                  <textarea
                    data-testid="studio-lyrics-textarea"
                    value={lyrics}
                    onChange={(e) => setLyrics(e.target.value)}
                    rows={6}
                    className="mt-1 w-full border border-[#222] bg-[#050505] p-3 font-mono text-xs text-white outline-none focus:border-[#FF003C]"
                  />
                </div>
              )}
            </div>

            <div className="p-5">
              <div className="font-mono text-xs uppercase tracking-widest text-[#808080]">03 / Reference Photos</div>
              <div className="mt-3 flex items-center justify-between gap-3">
                <div className="font-display text-2xl">Visuals</div>
                <button
                  data-testid="studio-upload-image-btn"
                  onClick={() => imageInputRef.current?.click()}
                  className="btn-ghost"
                  disabled={busy.upload}
                >
                  <ImagePlus size={14} /> Add
                </button>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => Array.from(e.target.files || []).forEach((f) => onUpload(f, "image"))}
                />
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2">
                {images.map((img) => {
                  const isFace = faceRef?.imageId === img.id;
                  const isLoading = isFace && faceRef?.loading;
                  return (
                    <div key={img.id} className={`relative border ${isFace ? "border-[#FFE800]" : "border-[#222]"}`}>
                      <img src={`${API}/files/${img.id}`} alt={img.name} className="h-24 w-full object-cover" />
                      <button
                        onClick={() => setAsFaceReference(img.id)}
                        title={isFace ? "Remove face reference" : "Set as face reference"}
                        data-testid={`studio-face-ref-${img.id}`}
                        className={`absolute left-1 top-1 p-1 ${isFace ? "bg-[#FFE800] text-[#050505]" : "bg-black/70 text-white hover:bg-[#FFE800] hover:text-[#050505]"}`}
                      >
                        {isLoading ? <Loader2 className="animate-spin" size={12} /> : (isFace ? <CheckCircle2 size={12} /> : <UserRound size={12} />)}
                      </button>
                      <button
                        onClick={() => {
                          setImages((imgs) => imgs.filter((i) => i.id !== img.id));
                          if (faceRef?.imageId === img.id) setFaceRef(null);
                        }}
                        className="absolute right-1 top-1 bg-black/70 p-1 text-white hover:bg-[#FF003C]"
                      >
                        <X size={12} />
                      </button>
                      {isFace && (
                        <div className="absolute bottom-0 left-0 right-0 bg-[#FFE800] px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-[#050505]">
                          Face Ref
                        </div>
                      )}
                    </div>
                  );
                })}
                {images.length === 0 && (
                  <div className="col-span-3 border border-dashed border-[#222] p-6 text-center font-mono text-xs text-[#808080]">
                    No photos yet
                  </div>
                )}
              </div>
              {faceRef?.description && (
                <div className="mt-3 border border-[#FFE800] bg-[#FFE800]/5 p-3" data-testid="studio-face-ref-description">
                  <div className="mb-1 flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-[#FFE800]">
                    <UserRound size={10} /> Face Reference Description
                  </div>
                  <p className="font-mono text-[11px] leading-relaxed text-[#c8c8c8]">
                    {faceRef.description}
                  </p>
                </div>
              )}
              {images.length > 0 && !faceRef && (
                <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-[#808080]">
                  Tip: click the person icon on a photo to lock in a face reference across every scene
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: storyboard + render */}
          <div className="md:col-span-7">
            <div className="frame border-l-0 border-t border-[#222] md:border-l">
              <div className="flex items-center justify-between border-b border-[#222] p-5">
                <div>
                  <div className="font-mono text-xs uppercase tracking-widest text-[#808080]">04 / Storyboard</div>
                  <div className="font-display text-3xl">
                    {scenes.length > 0 ? (
                      <input
                        data-testid="studio-title-input"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="w-full bg-transparent outline-none"
                      />
                    ) : (
                      "No scenes yet"
                    )}
                  </div>
                </div>
                <div className="font-mono text-xs text-[#808080]">
                  {scenes.length} scenes // {totalDuration}s
                </div>
              </div>

              <div className="max-h-[520px] overflow-y-auto p-5">
                {scenes.length === 0 ? (
                  <div className="border border-dashed border-[#222] p-10 text-center">
                    <div className="font-mono text-xs uppercase tracking-widest text-[#808080]">
                      Generate a storyboard to see scenes here
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {scenes.map((sc, i) => (
                      <div key={i} className="border border-[#222] bg-[#0A0A0B] p-4">
                        <div className="flex items-center justify-between">
                          <div className="font-mono text-xs uppercase tracking-widest text-[#FFE800]">
                            Scene {String(i + 1).padStart(2, "0")} · {sc.duration}s
                          </div>
                          <button
                            onClick={() => removeScene(i)}
                            className="text-[#808080] hover:text-[#FF003C]"
                            data-testid={`studio-remove-scene-${i}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                        <textarea
                          data-testid={`studio-scene-prompt-${i}`}
                          value={sc.prompt}
                          onChange={(e) => updateScene(i, "prompt", e.target.value)}
                          rows={3}
                          className="mt-2 w-full border border-[#222] bg-[#050505] p-2 font-mono text-xs text-white outline-none focus:border-[#FF003C]"
                        />
                        <div className="mt-2 flex gap-2">
                          {[4, 8, 12].map((d) => (
                            <button
                              key={d}
                              onClick={() => updateScene(i, "duration", d)}
                              className={`border px-3 py-1 font-mono text-[10px] uppercase tracking-widest ${
                                sc.duration === d
                                  ? "border-[#FF003C] bg-[#FF003C] text-white"
                                  : "border-[#222] text-[#808080] hover:border-white"
                              }`}
                              data-testid={`studio-scene-${i}-dur-${d}`}
                            >
                              {d}s
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="border-t border-[#222] p-5">
                {renderLocked ? (
                  <Link to="/pricing" data-testid="studio-render-locked-btn" className="btn-brutal w-full">
                    <Lock size={16} /> Unlock Unlimited Renders · Go Pro $15.99
                  </Link>
                ) : (
                  <button
                    data-testid="studio-render-btn"
                    onClick={renderVideo}
                    disabled={busy.render || !canRender || (job && job.status !== "ready" && job.status !== "failed")}
                    className="btn-brutal w-full"
                  >
                    {busy.render ? <Loader2 className="animate-spin" size={16} /> : <Film size={16} />}
                    {job && job.status === "rendering" ? "Rendering…" : "Render With Sora 2"}
                  </button>
                )}
                {!isPro && (
                  <div data-testid="studio-free-renders-note" className="mt-3 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-[#808080]">
                    <span>Free plan · {freeRendersLeft} test render{freeRendersLeft === 1 ? "" : "s"} left</span>
                    <Link to="/pricing" className="flex items-center gap-1 text-[#FFE800] hover:underline"><Crown size={11} /> Go Pro</Link>
                  </div>
                )}
                {job && (
                  <div className="mt-4 border border-[#222] p-4">
                    <div className="flex items-center justify-between font-mono text-xs uppercase tracking-widest text-[#808080]">
                      <span data-testid="studio-job-status">Status: {job.status}</span>
                      <span>{job.progress || 0}%</span>
                    </div>
                    <div className="mt-2 h-1 w-full bg-[#222]">
                      <div
                        className="h-1 bg-[#FF003C] transition-all duration-500"
                        style={{ width: `${job.progress || 0}%` }}
                      />
                    </div>
                    {job.error && (
                      <div className="mt-3 border border-[#FF003C] p-2 font-mono text-xs text-[#FF003C]">
                        {job.error}
                      </div>
                    )}
                    {job.status === "ready" && (
                      <div className="mt-4">
                        {job.clip_file_ids?.length > 0 && (
                          <VideoPlayer clips={job.clip_file_ids} audioId={audio?.id} />
                        )}
                        <div className="mt-3 flex flex-wrap gap-2">
                          {job.clip_file_ids?.map((cid, i) => (
                            <a
                              key={cid}
                              href={`${API}/files/${cid}/download`}
                              className="btn-ghost"
                              data-testid={`studio-download-clip-${i}`}
                            >
                              <Download size={12} /> Download Clip {i + 1}
                            </a>
                          ))}
                          <Link
                            to="/gallery"
                            data-testid="studio-goto-gallery"
                            className="btn-ghost"
                          >
                            <Play size={12} /> Open Gallery
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sticky action bar */}
      <div
        data-testid="studio-mobile-bar"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-[#222] bg-[#050505]/95 p-3 backdrop-blur-xl md:hidden"
        style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
      >
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1 font-mono text-[10px] uppercase tracking-widest text-[#808080]">
            <div className="truncate text-white">{scenes.length} scene{scenes.length === 1 ? "" : "s"} · {totalDuration}s{audio ? " · audio ✓" : ""}</div>
            <div className="truncate">{job ? `Render: ${job.status} ${job.progress || 0}%` : isPro ? "PRO · unlimited renders" : `Free · ${freeRendersLeft} render${freeRendersLeft === 1 ? "" : "s"} left`}</div>
          </div>
          {scenes.length === 0 ? (
            <button
              data-testid="studio-mobile-storyboard-btn"
              onClick={generateStoryboard}
              disabled={busy.storyboard || !canGenerateStoryboard}
              className="btn-brutal !min-h-[44px] !px-4 !py-2 !text-base disabled:opacity-50"
            >
              {busy.storyboard ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />} Storyboard
            </button>
          ) : renderLocked ? (
            <Link to="/pricing" data-testid="studio-mobile-locked-btn" className="btn-brutal !min-h-[44px] !px-4 !py-2 !text-base">
              <Lock size={16} /> Go Pro
            </Link>
          ) : (
            <button
              data-testid="studio-mobile-render-btn"
              onClick={renderVideo}
              disabled={busy.render || !canRender || (job && job.status !== "ready" && job.status !== "failed")}
              className="btn-brutal !min-h-[44px] !px-4 !py-2 !text-base disabled:opacity-50"
            >
              {busy.render ? <Loader2 className="animate-spin" size={16} /> : <Film size={16} />} Render
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Selector({ label, value, onChange, options, render, testid }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#808080]">{label}</div>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full border border-[#222] bg-[#050505] p-2 font-mono text-sm text-white focus:border-[#FF003C] outline-none"
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {render ? render(o) : o}
          </option>
        ))}
      </select>
    </div>
  );
}

function VideoPlayer({ clips, audioId }) {
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    setIdx(0);
  }, [clips]);

  const onEnded = () => {
    if (idx < clips.length - 1) setIdx(idx + 1);
    else if (audioRef.current) audioRef.current.pause();
  };

  const onPlay = () => {
    if (audioRef.current && audioRef.current.paused) audioRef.current.play().catch(() => {});
  };
  const onPause = () => {
    if (audioRef.current) audioRef.current.pause();
  };

  return (
    <div className="relative">
      <video
        ref={videoRef}
        data-testid="studio-video-player"
        src={`${API}/files/${clips[idx]}`}
        controls
        autoPlay
        onEnded={onEnded}
        onPlay={onPlay}
        onPause={onPause}
        className="w-full border border-[#222]"
      />
      {audioId && (
        <audio ref={audioRef} src={`${API}/files/${audioId}`} preload="auto" />
      )}
      <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-[#808080]">
        Clip {idx + 1} / {clips.length}
      </div>
    </div>
  );
}
